/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { ActionRequest, ActionSuccess } from '@wit/vtv-x-foundation-lib';
import { SagaIterator, Task } from 'redux-saga';
import {
  all,
  call,
  CallEffect,
  cancel,
  delay,
  fork,
  put,
  race,
  SagaReturnType,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import { StoreState } from '../store';

const ROUTINE_EXECUTION_TIME = 250;
const ANIMATION_DURATION = 480 * 2;
const TICKS_PER_EXECUTION_ROUTINE = 2;

enum ActionType {
  START_AUTO_PROGRESS = 'app/layoutProgressBar/START_AUTO_PROGRESS',
  AUTO_PROGRESS_STARTED = 'app/layoutProgressBar/AUTO_PROGRESS_STARTED',
  FINISH = 'app/layoutProgressBar/FINISH',
  FINISHED = 'app/layoutProgressBar/FINISHED',
  RESET = 'app/layoutProgressBar/RESET',
  REQUEST_PERCENTAGE_CHANGE = 'app/layoutProgressBar/REQUEST_PERCENTAGE_CHANGE',
  SET_PERCENTAGE = 'app/layoutProgressBar/SET_PERCENTAGE',
}

type StartAutoProgress = ActionRequest<ActionType.START_AUTO_PROGRESS>;
type AutoProgressStarted = ActionRequest<ActionType.AUTO_PROGRESS_STARTED>;
type Finish = ActionRequest<ActionType.FINISH>;
type Finished = ActionRequest<ActionType.FINISHED>;
type Reset = ActionRequest<ActionType.RESET>;
type RequestPercentageChange = ActionRequest<ActionType.REQUEST_PERCENTAGE_CHANGE, number>;
type SetPercentage = ActionSuccess<ActionType.SET_PERCENTAGE, number>;

type Actions =
  | StartAutoProgress
  | AutoProgressStarted
  | Finish
  | Finished
  | Reset
  | RequestPercentageChange
  | SetPercentage;

interface State {
  inExecution: boolean;
  isFinished: boolean; // all tasks completed
  isFinishing: boolean; // finish request received. waiting until de animation timeout
  progressPercentage: number;
}

const initialState: State = {
  inExecution: false,
  isFinished: false,
  isFinishing: false,
  progressPercentage: 0,
};

const Actions = {
  startAutoProgress: (): StartAutoProgress => ({ type: ActionType.START_AUTO_PROGRESS }),
  autoProgressStarted: (): AutoProgressStarted => ({ type: ActionType.AUTO_PROGRESS_STARTED }),
  finish: (): Finish => ({ type: ActionType.FINISH }),
  finished: (): Finished => ({ type: ActionType.FINISHED }),
  reset: (): Reset => ({ type: ActionType.RESET }),
  requestPercentageChange: (meta: number): RequestPercentageChange => ({
    type: ActionType.REQUEST_PERCENTAGE_CHANGE,
    meta,
  }),
  setPercentage: (payload: number): SetPercentage => ({
    type: ActionType.SET_PERCENTAGE,
    payload,
  }),
};

export const ActionCreators = {
  startAutoProgress: Actions.startAutoProgress,
  finish: Actions.finish,
  requestPercentageChange: Actions.requestPercentageChange,
};

export const Selectors = {
  getInExecution: (store: StoreState): boolean => store.layoutProgressBar.inExecution,
  getIsFinished: (store: StoreState): boolean => store.layoutProgressBar.isFinished,
  getIsFinishing: (store: StoreState): boolean => store.layoutProgressBar.isFinishing,
  getProgressPercentage: (store: StoreState): number => store.layoutProgressBar.progressPercentage,
};

export default function layoutProgressBar(state = initialState, action: Actions): State {
  switch (action.type) {
    case ActionType.AUTO_PROGRESS_STARTED:
      return { ...state, inExecution: true, isFinished: false, progressPercentage: 0 };

    case ActionType.FINISH:
      return { ...state, inExecution: false, isFinishing: true, progressPercentage: 100 };

    case ActionType.FINISHED:
      return { ...state, isFinishing: false, isFinished: true };

    case ActionType.SET_PERCENTAGE: {
      return { ...state, progressPercentage: action.payload };
    }
    case ActionType.RESET:
      return initialState;
    default:
      return state;
  }
}

function* setPercentage(value: number): SagaIterator {
  if (value >= 100) {
    yield put(Actions.finish());
  }

  yield put(Actions.setPercentage(value));
}

function* requestPercentageSaga(action: RequestPercentageChange): SagaIterator {
  const { meta } = action;

  yield call(setPercentage, meta);
}

let autoProgressTask: Task | undefined;

/**
 * This function is an hyperbole with asymptote in 100 that calculates an percentage f(x) that slows down when x increases
 * Once it's asymptote is 100, it never reaches that value
 * @param x x value
 * @returns percentage ( f(x) )
 */

const percentageGenerator = (x: number) => -(1000 * (1 / (x + 10))) + 100;

function* autoProgressCalculationRoutine(): SagaIterator {
  // when the previous progress animation is finishing waits it completes
  if (yield select(Selectors.getIsFinishing)) {
    yield race([take(ActionType.FINISHED), take(ActionType.RESET)]);
  }

  // start the current progress status
  yield put(Actions.autoProgressStarted());

  let ticks = 0;

  while (true) {
    const inExecution: boolean = yield select(Selectors.getInExecution);
    if (!inExecution) {
      return;
    }

    ticks += TICKS_PER_EXECUTION_ROUTINE;
    yield call(setPercentage, percentageGenerator(ticks));
    yield delay(ROUTINE_EXECUTION_TIME);
  }
}

function* resetToInitialState(): SagaIterator {
  yield delay(ANIMATION_DURATION);

  const inExecution: boolean = yield select(Selectors.getInExecution);

  if (inExecution) {
    yield put(Actions.finished());
    return;
  }

  yield put(Actions.reset());
}

function* finishProgressSaga(): SagaIterator {
  if (autoProgressTask) {
    yield cancel(autoProgressTask);
    autoProgressTask = undefined;
  }
  yield call(resetToInitialState);
}

function* autoProgressSaga(): SagaIterator {
  autoProgressTask = yield fork(autoProgressCalculationRoutine);
}

export function* saga(): SagaIterator {
  yield all([
    takeEvery(ActionType.REQUEST_PERCENTAGE_CHANGE, requestPercentageSaga),
    takeLatest(ActionType.START_AUTO_PROGRESS, autoProgressSaga),
    takeLatest(ActionType.FINISH, finishProgressSaga),
  ]);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function* withProgress(fn: any, args: any[]): SagaIterator {
  yield put(Actions.startAutoProgress());
  try {
    return yield call(fn, ...args);
  } finally {
    yield put(Actions.finish());
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function callWithProgress<Fn extends (...fnArgs: any[]) => SagaIterator>(
  fn: Fn,
  ...args: Parameters<Fn>
): CallEffect<SagaReturnType<Fn>> {
  return call(withProgress, fn, args);
}
